import type { Avatar } from '@oefy/core';
import type { ReactNode } from 'react';
import { useId } from 'react';
import { MascotBust } from './mascot';

const INK = '#0f172a';
const BLUSH = '#fda4af';

function Eye({ x, y, size }: { readonly x: number; readonly y: number; readonly size: number }) {
  return (
    <g>
      <circle cx={x} cy={y} r={size} fill="#fff" />
      <circle cx={x + size * 0.12} cy={y + size * 0.12} r={size * 0.62} fill={INK} />
      <circle cx={x - size * 0.16} cy={y - size * 0.2} r={size * 0.24} fill="#fff" />
    </g>
  );
}

function Cheeks({ y, spread = 21 }: { readonly y: number; readonly spread?: number }) {
  return (
    <g fill={BLUSH} opacity="0.75">
      <circle cx={50 - spread} cy={y} r="3.6" />
      <circle cx={50 + spread} cy={y} r="3.6" />
    </g>
  );
}

function Shoulders({ fill }: { readonly fill: string }) {
  return <path d="M22 104 C24 90 36 85 50 86 C64 85 76 90 78 104 Z" fill={fill} />;
}

function Worm() {
  const id = useId();
  return (
    <g transform="translate(-8 6.7) scale(0.185)">
      <MascotBust clipId={id} />
    </g>
  );
}

const fox = (
  <>
    <Shoulders fill="#ea580c" />
    <path d="M21 46 L28 13 L47 33 Z" fill="#f97316" />
    <path d="M79 46 L72 13 L53 33 Z" fill="#f97316" />
    <path d="M27 38 L30 21 L41 32 Z" fill="#fed7aa" />
    <path d="M73 38 L70 21 L59 32 Z" fill="#fed7aa" />
    <path
      d="M18 53 C18 35 33 27 50 27 C67 27 82 35 82 53 C82 72 67 86 50 89 C33 86 18 72 18 53 Z"
      fill="#f97316"
    />
    <path
      d="M24 60 C33 60 42 64 50 72 C58 64 67 60 76 60 C73 77 63 87 50 89 C37 87 27 77 24 60 Z"
      fill="#fff7ed"
    />
    <Eye x={37} y={52} size={6.4} />
    <Eye x={63} y={52} size={6.4} />
    <ellipse cx="50" cy="72" rx="5" ry="3.8" fill={INK} />
    <Cheeks y={66} spread={22} />
  </>
);

const owl = (
  <>
    <path
      d="M23 15 C29 20 35 24 40 27 C46 25 54 25 60 27 C65 24 71 20 77 15 C80 25 81 34 80 43 C85 58 84 82 72 96 C64 104 36 104 28 96 C16 82 15 58 20 43 C19 34 20 25 23 15 Z"
      fill="#a16207"
    />
    <path d="M25 22 C29 26 33 29 37 30 C32 33 28 37 26 41 C25 35 24 28 25 22 Z" fill="#854d0e" />
    <path d="M75 22 C71 26 67 29 63 30 C68 33 72 37 74 41 C75 35 76 28 75 22 Z" fill="#854d0e" />
    <path d="M22 66 C17 74 18 86 26 94 C28 84 29 75 27 64 Z" fill="#854d0e" />
    <path d="M78 66 C83 74 82 86 74 94 C72 84 71 75 73 64 Z" fill="#854d0e" />
    <ellipse cx="50" cy="84" rx="18" ry="16" fill="#fde68a" />
    <path
      d="M42 79 q2 2.5 4 0 M54 79 q2 2.5 4 0 M48 86 q2 2.5 4 0 M42 92 q2 2.5 4 0 M54 92 q2 2.5 4 0"
      stroke="#d4a017"
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M50 39 C44 33 24 33 23 51 C22 63 32 68 41 66 C45 65 48 63 50 61 C52 63 55 65 59 66 C68 68 78 63 77 51 C76 33 56 33 50 39 Z"
      fill="#fef3c7"
    />
    <Eye x={37} y={51} size={9} />
    <Eye x={63} y={51} size={9} />
    <path d="M45.5 60 Q50 58.5 54.5 60 L50 68 Z" fill="#f97316" />
  </>
);

const cat = (
  <>
    <Shoulders fill="#64748b" />
    <path d="M21 48 L25 15 L46 32 Z" fill="#94a3b8" />
    <path d="M79 48 L75 15 L54 32 Z" fill="#94a3b8" />
    <path d="M26 41 L28 22 L40 32 Z" fill="#fecdd3" />
    <path d="M74 41 L72 22 L60 32 Z" fill="#fecdd3" />
    <ellipse cx="50" cy="57" rx="31" ry="28" fill="#94a3b8" />
    <path
      d="M43 31 l2 8 M50 30 v9 M57 31 l-2 8"
      stroke="#64748b"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <ellipse cx="44" cy="67" rx="8" ry="6" fill="#e2e8f0" />
    <ellipse cx="56" cy="67" rx="8" ry="6" fill="#e2e8f0" />
    <Eye x={38} y={53} size={6.5} />
    <Eye x={62} y={53} size={6.5} />
    <path d="M46.5 62 h7 l-3.5 4 z" fill="#fb7185" />
    <path
      d="M31 65 H17 M32 70 L19 74 M69 65 H83 M68 70 L81 74"
      stroke="#475569"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </>
);

const bear = (
  <>
    <Shoulders fill="#92400e" />
    <circle cx="26" cy="32" r="11" fill="#b45309" />
    <circle cx="74" cy="32" r="11" fill="#b45309" />
    <circle cx="26" cy="32" r="5.5" fill="#fde68a" />
    <circle cx="74" cy="32" r="5.5" fill="#fde68a" />
    <circle cx="50" cy="57" r="31" fill="#b45309" />
    <ellipse cx="50" cy="69" rx="14" ry="11" fill="#fde68a" />
    <Eye x={38} y={52} size={6.2} />
    <Eye x={62} y={52} size={6.2} />
    <ellipse cx="50" cy="64" rx="5.5" ry="4" fill={INK} />
    <path
      d="M50 68 v3.5 M45.5 72 q4.5 4 9 0"
      stroke={INK}
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <Cheeks y={64} spread={20} />
  </>
);

const bunny = (
  <>
    <Shoulders fill="#e2e8f0" />
    <g transform="rotate(-9 37 42)">
      <rect x="30" y="4" width="14" height="42" rx="7" fill="#f8fafc" />
      <rect x="34" y="10" width="6" height="30" rx="3" fill="#fecdd3" />
    </g>
    <g transform="rotate(9 63 42)">
      <rect x="56" y="4" width="14" height="42" rx="7" fill="#f8fafc" />
      <rect x="60" y="10" width="6" height="30" rx="3" fill="#fecdd3" />
    </g>
    <circle cx="50" cy="61" r="28" fill="#f8fafc" />
    <Eye x={40} y={57} size={5.6} />
    <Eye x={60} y={57} size={5.6} />
    <ellipse cx="50" cy="67" rx="3.6" ry="2.8" fill="#fb7185" />
    <path
      d="M50 70 q-4 4 -7 1 M50 70 q4 4 7 1"
      stroke={INK}
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
    />
    <rect x="47.5" y="71" width="5" height="5" rx="1.2" fill="#fff" stroke="#cbd5e1" />
    <Cheeks y={67} spread={18} />
  </>
);

const frog = (
  <>
    <Shoulders fill="#16a34a" />
    <circle cx="33" cy="37" r="13.5" fill="#22c55e" />
    <circle cx="67" cy="37" r="13.5" fill="#22c55e" />
    <ellipse cx="50" cy="61" rx="34" ry="26" fill="#22c55e" />
    <Eye x={33} y={37} size={8.5} />
    <Eye x={67} y={37} size={8.5} />
    <circle cx="46" cy="55" r="1.6" fill="#14532d" />
    <circle cx="54" cy="55" r="1.6" fill="#14532d" />
    <path
      d="M31 64 Q50 81 69 64"
      stroke="#14532d"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
    />
    <Cheeks y={63} spread={25} />
  </>
);

const panda = (
  <>
    <Shoulders fill="#1e293b" />
    <circle cx="26" cy="32" r="11" fill="#1e293b" />
    <circle cx="74" cy="32" r="11" fill="#1e293b" />
    <circle cx="50" cy="57" r="31" fill="#fff" />
    <ellipse cx="38" cy="54" rx="9" ry="11" transform="rotate(-25 38 54)" fill="#1e293b" />
    <ellipse cx="62" cy="54" rx="9" ry="11" transform="rotate(25 62 54)" fill="#1e293b" />
    <Eye x={39} y={53} size={4.8} />
    <Eye x={61} y={53} size={4.8} />
    <ellipse cx="50" cy="65" rx="4.6" ry="3.3" fill={INK} />
    <path
      d="M50 68 v3 M46 72 q4 3 8 0"
      stroke={INK}
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <Cheeks y={68} spread={20} />
  </>
);

const art: Record<Avatar, ReactNode> = { worm: <Worm />, fox, owl, cat, bear, bunny, frog, panda };

export const avatarNames: Record<Avatar, string> = {
  worm: 'Worm',
  fox: 'Vos',
  owl: 'Uil',
  cat: 'Kat',
  bear: 'Beer',
  bunny: 'Konijn',
  frog: 'Kikker',
  panda: 'Panda',
};

export function AvatarArt({
  avatar,
  className,
}: {
  readonly avatar: Avatar;
  readonly className?: string;
}) {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true" className={className}>
      {art[avatar]}
    </svg>
  );
}
